import { Router } from '@reach/router';
import Loading from 'components/Loading';
import React, { lazy, Suspense } from 'react';

const LoginLayout = lazy(() => import('layouts/Login'));
const AdminLayout = lazy(() => import('layouts/Admin'));
const UserLayout = lazy(() => import('layouts/User'));

const UserHome = lazy(() => import('screens/User/Home'));
const Invoices = lazy(() => import('screens/User/Invoices'));
const DeliveryNotes = lazy(() => import('screens/User/DeliveryNotes'));

const AdminHome = lazy(() => import('screens/Admin/Home'));
const ManageAdmins = lazy(() => import('screens/Admin/ManageAdmins'));
const ManageAdminRights = lazy(() => import('screens/Admin/ManageAdminRights'));
const ManageCompanies = lazy(() => import('screens/Admin/ManageCompanies'));
const EditCompanyDetails = lazy(() => import('screens/Admin/EditCompany/EditCompanyDetails'));
const EditCompanyCoatings = lazy(() => import('screens/Admin/EditCompany/EditCompanyCoatings'));
const AddCompany = lazy(() => import('screens/Admin/AddCompany'));
const CoatingConfiguratorCoatings = lazy(() => import('screens/Admin/CoatingConfiguratorCoatings'));
const EditCoatingConfiguratorCoating = lazy(() => import('screens/Admin/EditCoatingConfiguratorCoating'));
const CoatingConfiguratorCategories = lazy(() => import('screens/Admin/CoatingConfiguratorCategories'));
const EditCoatingConfiguratorCategory = lazy(() => import('screens/Admin/EditCoatingConfiguratorCategory'));
const CoatingConfiguratorSteps = lazy(() => import('screens/Admin/CoatingConfiguratorSteps'));
const EditCoatingConfiguratorStep = lazy(() => import('screens/Admin/EditCoatingConfiguratorStep'));
const ManageBranches = lazy(() => import('screens/Admin/ManageBranches'));
const AddBranch = lazy(() => import('screens/Admin/AddBranch'));
const EditBranch = lazy(() => import('screens/Admin/EditBranch'));
const CoatingConfiguratorProcedures = lazy(() => import('screens/Admin/CoatingConfiguratorProcedures'));
const EditCoatingConfiguratorProcedure = lazy(() => import('screens/Admin/EditCoatingConfiguratorProcedure'));
const EditCompanyProcedures = lazy(() => import('screens/Admin/EditCompanyProcedures'));
const ExpertAdviser = lazy(() => import('screens/Admin/ExpertAdviser'));
const CustomerAdviser = lazy(() => import('screens/Admin/CustomerAdviser'));
const ManageRightGroups = lazy(() => import('screens/Admin/ManageRightGroups'));
const KnowledgeBase = lazy(() => import('screens/Admin/KnowledgeBase'));
const KnowledgeBaseCategories = lazy(() => import('screens/Admin/KnowledgeBaseCategories'));
const KnowledgeBaseStepDetail = lazy(() => import('screens/Admin/KnowledgeBaseDetail/step'));
const KnowledgeBaseCoatingDetail = lazy(() => import('screens/Admin/KnowledgeBaseDetail/coating'));
const EditCompanyRights = lazy(() => import('screens/Admin/EditCompany/EditCompanyRights'));
const ManageCompanyUsers = lazy(() => import('screens/Admin/ManageCompanyUsers'));

const Login = lazy(() => import('screens/Account/Login'));
const LoginWithUsername = lazy(() => import('screens/Account/LoginWithUsername'));
const PasswordForgotten = lazy(() => import('screens/Account/PasswordForgotten'));
const ResetPassword = lazy(() => import('screens/Account/ResetPassword'));
const RegisterAdmin = lazy(() => import('screens/Account/Register/Admin'));
const RegisterCompanyUer = lazy(() => import('screens/Account/Register/CompanyUser'));
const SignUpCompanyUser = lazy(() => import('screens/Account/SignUp'));

const App = () => {
    return (
        <Suspense fallback={<Loading />}>
            <Router>
                <LoginLayout path="account">
                    <Login path="login" />
                    <Login path="login/:redirectUrl" />
                    {
                        process.env.NODE_ENV === 'development' &&
                        <LoginWithUsername path="loginwithusername/:username" />
                    }
                    <PasswordForgotten path="password-forgotten" />
                    <PasswordForgotten path="password-forgotten-confirm" step={2} />
                    <ResetPassword path="reset-password" />
                    <RegisterAdmin path="register/admin/:code" />
                    <RegisterCompanyUer path="register/companyuser/:code" />
                    <SignUpCompanyUser path="signup" />
                </LoginLayout>
                <AdminLayout path="admin">
                    <AdminHome path="/" default />
                    <ManageAdmins path="admins" />
                    <ManageAdminRights path="admins/:id/roles" />
                    <CoatingConfiguratorCoatings path="coating-configurator/coatings" />
                    <EditCoatingConfiguratorCoating path="coating-configurator/coatings/:id" />
                    <ManageCompanies path="companies" />
                    <EditCompanyDetails path="companies/:id/details" />
                    <EditCompanyCoatings path="companies/:id/coatings" />
                    <AddCompany path="companies/add" />
                    <CoatingConfiguratorCategories path="coating-configurator/categories" />
                    <EditCoatingConfiguratorCategory path="coating-configurator/categories/:id/edit" />
                    <CoatingConfiguratorSteps path="coating-configurator/steps" />
                    <EditCoatingConfiguratorStep path="coating-configurator/steps/:id/edit" />
                    <ManageBranches path="branches" />
                    <AddBranch path="branches/add" />
                    <EditBranch path="branches/:id/edit" />
                    <CoatingConfiguratorProcedures path="coating-configurator/procedures" />
                    <EditCoatingConfiguratorProcedure path="coating-configurator/procedures/:id/edit" />
                    <EditCompanyProcedures path="companies/:companyId/procedures" />
                    <ExpertAdviser path="expert-adviser" />
                    <CustomerAdviser path="customer-adviser" />
                    <ManageRightGroups path="roles" />
                    <KnowledgeBase path="knowledge-base" />
                    <KnowledgeBaseCategories path="knowledge-base/categories" />
                    <KnowledgeBaseStepDetail path="knowledge-base/step/:id/detail" />
                    <KnowledgeBaseCoatingDetail path="knowledge-base/coating/:id/detail" />
                    <EditCompanyRights path="companies/:id/rights" />
                    <ManageCompanyUsers path="companies/:id/users" />
                </AdminLayout>
                <UserLayout path="/">
                    <UserHome default path="/" />
                    <CustomerAdviser path="customer-adviser" />
                    <KnowledgeBase path="knowledge-base" />
                    <KnowledgeBaseStepDetail path="knowledge-base/step/:id/detail" />
                    <KnowledgeBaseCoatingDetail path="knowledge-base/coating/:id/detail" />
                    <Invoices path="invoices" />
                    <DeliveryNotes path="deliverynotes" />
                </UserLayout>
            </Router>
        </Suspense>
    );
};

export default App;
